import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Grid
} from '@mui/material';
import { embeddedEmissionService } from '../services/embeddedEmissionService';
import { eledgerService } from '../services/eledgerService';

const SOURCES = ['supplier_provided', 'mav_applied', 'industry_average', 'calculated'];

function EmbeddedEmissions() {
  const [ledgers, setLedgers] = useState([]);
  const [selectedLedger, setSelectedLedger] = useState('');
  const [allEmissions, setAllEmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    supplier_name: '', product_name: '', reception_date: '', quantity: '',
    embedded_co2e_kg: '', source: 'supplier_provided', purchase_order_reference: ''
  });

  // Derived: filter by selected ledger, or show all
  const emissions = selectedLedger
    ? allEmissions.filter(e => e.e_ledger_id === selectedLedger)
    : allEmissions;

  useEffect(() => {
    eledgerService.getAll().then(data => setLedgers(data ?? [])).catch(() => {});
    loadAllEmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllEmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await embeddedEmissionService.getAll();
      setAllEmissions(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await embeddedEmissionService.record(selectedLedger, {
        ...form,
        quantity: parseFloat(form.quantity),
        embedded_co2e_kg: parseFloat(form.embedded_co2e_kg)
      });
      setDialogOpen(false);
      setForm({ supplier_name: '', product_name: '', reception_date: '', quantity: '', embedded_co2e_kg: '', source: 'supplier_provided', purchase_order_reference: '' });
      loadAllEmissions();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  const getSourceColor = (src) => {
    const map = { supplier_provided: 'success', mav_applied: 'warning', industry_average: 'info', calculated: 'default' };
    return map[src] || 'default';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Embedded Emissions</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Scope 3 upstream emissions from suppliers (E-ledger Principles 3-4, PACT integration)
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Select E-Ledger</InputLabel>
              <Select value={selectedLedger} label="Select E-Ledger" onChange={(e) => setSelectedLedger(e.target.value)}>
                {ledgers.map(l => <MenuItem key={l.e_ledger_id} value={l.e_ledger_id}>{l.ledger_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button variant="contained" disabled={!selectedLedger} onClick={() => setDialogOpen(true)}>
              Record Embedded Emission
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Embedded Emissions ({emissions.length})
            {emissions.length > 0 && (
              <Chip label={`${emissions.reduce((s, e) => s + parseFloat(e.embedded_co2e_kg), 0).toFixed(2)} kg CO2e total`}
                size="small" color="info" sx={{ ml: 2 }} />
            )}
          </Typography>
          {emissions.length === 0 ? (
            <Alert severity="info">No embedded emissions recorded{selectedLedger ? ' for this ledger' : ''}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {!selectedLedger && <TableCell>E-Ledger</TableCell>}
                  <TableCell>Supplier</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Reception</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">CO2e (kg)</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>PO Ref</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emissions.map((em) => (
                  <TableRow key={em.embedded_emission_id}>
                    {!selectedLedger && <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{em.ledger?.ledger_name || '—'}</TableCell>}
                    <TableCell>{em.supplier_name}</TableCell>
                    <TableCell>{em.product_name}</TableCell>
                    <TableCell>{em.reception_date}</TableCell>
                    <TableCell align="right">{parseFloat(em.quantity).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{parseFloat(em.embedded_co2e_kg).toFixed(2)}</TableCell>
                    <TableCell><Chip label={em.source.replace(/_/g, ' ')} size="small" color={getSourceColor(em.source)} /></TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{em.purchase_order_reference}</TableCell>
                    <TableCell><Chip label={em.verification_status} size="small" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Embedded Emission (Scope 3)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Supplier Name" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Product Name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Reception Date" type="date" InputLabelProps={{ shrink: true }}
                value={form.reception_date} onChange={(e) => setForm({ ...form, reception_date: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Quantity" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="CO2e (kg)" type="number" value={form.embedded_co2e_kg} onChange={(e) => setForm({ ...form, embedded_co2e_kg: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select value={form.source} label="Source" onChange={(e) => setForm({ ...form, source: e.target.value })}>
                  {SOURCES.map(s => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="PO Reference" value={form.purchase_order_reference}
                onChange={(e) => setForm({ ...form, purchase_order_reference: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={!form.supplier_name || !form.product_name || !form.reception_date || !form.quantity || !form.embedded_co2e_kg || !form.purchase_order_reference}>
            Record
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmbeddedEmissions;
